import { ReactNode } from 'react';
import { Entries } from '@mews/std';

export type { CellContext, RowData } from '@tanstack/react-table';

declare module '@tanstack/react-table' {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-unnecessary-type-constraint
    interface ColumnMeta<TData extends unknown, TValue> {
        filtersType?: ColumnFilterType;
        getMultiSelectOptions?: () => MultiSelectOption[];
        getBoolOptions?: () => BoolOptions;
        align?: 'start' | 'end';
        width?: number;
        showHeaderInColumnFilter?: boolean;
    }
}

export type TableFilters<T> = {
    [key in keyof T]?: ColumnFilters;
};

export interface TableState<T, F extends TableFilters<T> = TableFilters<T>> {
    pagination: PaginationTypes;
    sort: {
        [key in keyof T]?: ColumnSort;
    };
    filters: F;
    globalFilters: GlobalFilters;
}

interface GlobalFilters {
    search?: string;
}

export interface PaginationTypes {
    pageSize: PaginationSize;
    pageIndex: number;
    show?: boolean;
}

export interface ColumnSort {
    order: Order;
    priority: number;
}

export enum Order {
    Ascending = 'Ascending',
    Descending = 'Descending',
}

export type ColumnFilters = StringFilters | NumberFilters | MultiSelectFilters | BoolFilters | null;
export type ColumnFilter =
    | Filter<StringFilterType, string>
    | Filter<NumberFilterType, number>
    | Filter<EnumFilterType, string[]>
    | Filter<BoolFilterType, boolean>
    | null;

export interface Filter<Type, Value> {
    filterType: Type;
    value: Value;
}

export enum StringFilterType {
    Contains = 'Contains',
    Equals = 'Equals',
}

export type StringFilters = Filter<StringFilterType, string>[];

export enum NumberFilterType {
    Equals = 'Equals',
    GreaterThan = 'GreaterThan',
    GreaterThanEquals = 'GreaterThanEquals',
    LowerThan = 'LowerThan',
    LowerThanEquals = 'LowerThanEquals',
}

export type NumberFilters = Filter<NumberFilterType, number>[];

export interface MultiSelectOption {
    value: string;
    label: string;
}

export interface BoolOptions {
    true: string | ReactNode;
    false: string | ReactNode;
    undefined: string | ReactNode;
}

export type MultiSelectFilters = Filter<EnumFilterType, string[]>[];

export enum EnumFilterType {
    In = 'In',
}

export enum BoolFilterType {
    Equals = 'Equals',
    NotEquals = 'NotEquals',
}

export type BoolFilters = Filter<BoolFilterType, boolean>[];

export enum ColumnFilterType {
    String,
    Number,
    MultiSelect,
    Bool,
}

export type ColumnFilterEntries<T> = Entries<TableFilters<T>>;

export type InfinityType = number & { Infinity: true };

export type PaginationSize = keyof typeof PAGINATION_SIZES | InfinityType;

export const PAGINATION_SIZES = {
    25: 25,
    50: 50,
    75: 75,
    100: 100,
} as const;

export enum ReducerActionType {
    GlobalSearch = 'GlobalSearch',
    Pagination = 'Pagination',
    RemoveAllFilters = 'RemoveAllFilters',
    RemoveFilter = 'RemoveFilter',
    RemoveMultiSelectFilter = 'RemoveMultiSelectFilter',
    SetColumnFilter = 'SetColumnFilter',
    Sort = 'Sort',
}

export interface ReducerFilterPayload<T, F extends TableFilters<T>> {
    columnName: keyof F;
    columnFilter?: ColumnFilter;
    selectedValue?: string;
}

export type TableReducerAction<T, F extends TableFilters<T>> =
    | { type: ReducerActionType.GlobalSearch; payload: GlobalFilters }
    | { type: ReducerActionType.Pagination; payload: Partial<PaginationTypes> }
    | { type: ReducerActionType.RemoveAllFilters; payload?: never }
    | { type: ReducerActionType.RemoveFilter; payload: ReducerFilterPayload<T, F> }
    | { type: ReducerActionType.RemoveMultiSelectFilter; payload: ReducerFilterPayload<T, F> }
    | { type: ReducerActionType.SetColumnFilter; payload: ReducerFilterPayload<T, F> }
    | {
          type: ReducerActionType.Sort;
          payload: {
              columnName: keyof T;
              currentOrder?: Order;
          };
      };
